import { useEffect, useState } from "react";
import { FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { EMPTY_CLIENT, invoiceLabel } from "../../lib/invoicing";
import { printInvoiceTicket } from "../../lib/invoiceTicket";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { ClientPicker } from "./ClientPicker";
import { validClient } from "./FiscalClientFields";

const money = (n) => `$${(Number(n) || 0).toFixed(2)}`;

export function InvoiceDialogHost() {
  const [req, setReq] = useState(null);
  const [client, setClient] = useState(EMPTY_CLIENT);
  const [save, setSave] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const h = (e) => { setReq(e.detail); setClient(EMPTY_CLIENT); setSave(true); };
    window.addEventListener("open-invoice", h);
    return () => window.removeEventListener("open-invoice", h);
  }, []);

  const submit = async () => {
    const err = validClient(client);
    if (err) { toast.error(err); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/invoicing/orders/${req.orderId}/invoice`, { client, split_id: req.splitId || null, save_client: save });
      toast.success(`Factura ${invoiceLabel(data)} timbrada${data.email_sent ? " y enviada por correo" : ""}`);
      if (data.email_error) toast.warning(`No se pudo enviar el correo: ${data.email_error}`);
      printInvoiceTicket(data).catch(() => toast.error("No se pudo imprimir el ticket de la factura"));
      req.onDone && req.onDone(data);
      setReq(null);
      return;
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const skip = () => { req?.onSkip && req.onSkip(); setReq(null); };

  return (
    <Dialog open={!!req} onOpenChange={(o) => !o && !busy && skip()}>
      <DialogContent className="sm:max-w-xl max-h-[92vh] overflow-y-auto" data-testid="invoice-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl flex items-center gap-2"><FileText size={20} className="text-[#E85D04]" /> Facturar venta</DialogTitle>
          <DialogDescription>
            {req?.label ? `${req.label} · ` : ""}{req?.total ? `Total ${money(req.total)}` : ""}. Se timbra el CFDI 4.0 y se envía el PDF y XML al correo del cliente.
          </DialogDescription>
        </DialogHeader>
        <ClientPicker value={client} onChange={setClient} />
        <label className="flex items-center gap-2 text-sm mt-1">
          <input data-testid="invoice-save-client" type="checkbox" checked={save} onChange={(e) => setSave(e.target.checked)} className="w-4 h-4 accent-[#E85D04]" />
          Guardar datos del cliente para la próxima vez
        </label>
        <DialogFooter className="gap-2">
          <button data-testid="invoice-skip-btn" disabled={busy} onClick={skip} className="h-12 px-5 rounded-xl border border-[#111827]/15 font-semibold">Facturar después</button>
          <button data-testid="invoice-submit-btn" disabled={busy} onClick={submit}
            className="flex-1 h-12 rounded-xl bg-[#E85D04] text-white font-semibold hover:bg-[#C2410C] disabled:opacity-60 flex items-center justify-center gap-2">
            {busy ? <><Loader2 size={18} className="animate-spin" /> Timbrando…</> : "Timbrar factura"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
